'use client'

import { cn } from '@mntn-dev/ui-utilities'
import type { VirtualItem } from '@tanstack/react-virtual'
import type { KeyboardEvent, RefAttributes } from 'react'
import { SelectItemBase } from './select-item-base.tsx'
import { SelectOptionIcon } from './select-option-icon.tsx'
import { SelectOptionSelectedIndicator } from './select-option-selected-indicator.tsx'
import type { SelectOptionData } from './types.ts'
import { type UseSelectReturn, useSelectContext } from './use-select.ts'

export type SelectOptionProps<ValueType extends string> = {
  close: () => void
  item: SelectOptionData<ValueType>
  placeholder?: boolean
  loadingRow?: boolean
  virtualItem?: VirtualItem
} & RefAttributes<HTMLDivElement>

export const SelectOption = <ValueType extends string>({
  close,
  item,
  loadingRow,
  placeholder,
  ref,
  virtualItem,
}: SelectOptionProps<ValueType>) => {
  const context: UseSelectReturn<ValueType> = useSelectContext<ValueType>()

  const { optionHeight, value } = context

  const selected = value === item.value

  const handleClick = () => {
    if (loadingRow) {
      return
    }
    // if we are deselectable and we click on the placeholder, clear and close.
    if (placeholder && context.deselectable) {
      value === null ? close() : context.onChange(null)
      return
    }

    // if we are single select and we click on the selected item, just close.
    if (selected) {
      close()
      return
    }

    context.onChange(item.value as ValueType)
    close()
  }
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Space') {
      e.preventDefault()
      e.stopPropagation()

      handleClick()
    }
  }

  return (
    <SelectItemBase
      disabled={item.disabled}
      label={item.label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      optionHeight={optionHeight}
      ref={ref}
      selected={selected}
      virtualItem={virtualItem}
    >
      <SelectOptionSelectedIndicator selected={selected} />

      {item.icon && <SelectOptionIcon icon={item.icon} />}
      <span
        className={cn('text-primary font-base truncate flex-initial', {
          'text-disabled': item.disabled,
          'text-placeholder': placeholder,
        })}
      >
        {item.label}
      </span>
    </SelectItemBase>
  )
}
